import { Alert, AlertProps, AlertTitle, Typography } from '@mui/material'
import { ActionRequest, Device, Notification, RuuviTag } from '../types'
import { useEffect, useState } from 'react'
import { getRequests } from '../services/requestService'
import { RequestableType } from '../enums/RequestableType'
import { RequestStatus } from '../enums/RequestStatus'
import { ActionRequestAction } from '../enums/ActionRequestAction'
import ActionRequestAcceptButton from '../components/ActionRequestAcceptButton'
import { useTranslation } from 'react-i18next'
import { subscribeActionRequestNotifications } from '../subscribers'
import { EntityEventAction } from '../enums'
import { isActionRequestClosed } from '../utils/actionRequest'
import { subHours } from 'date-fns'

export interface EnableAlertNotificationsAlertProps
  extends Omit<AlertProps, 'children' | 'severity' | 'action'> {
  device?: Device
  ruuviTag?: RuuviTag
  afterAccept?: (actionRequest?: ActionRequest) => void
}

export default function EnableAlertNotificationsAlert({
  device,
  ruuviTag,
  afterAccept,
  ...props
}: EnableAlertNotificationsAlertProps): JSX.Element {
  /**
   * The translate function.
   */
  const { t } = useTranslation('actionRequest')

  /**
   * The action requests state.
   */
  const [actionRequests, setActionRequests] = useState<ActionRequest[]>([])

  /**
   * The action request.
   */
  const actionRequest = actionRequests.length > 0 ? actionRequests[0] : undefined

  /**
   * The loading state.
   */
  const [_loading, setLoading] = useState<boolean>(true)

  /**
   * The error state.
   */
  const [_error, setError] = useState<unknown>()

  /**
   * Load the device action requests.
   */
  async function loadDeviceActionRequests(deviceName: string, signal: AbortSignal) {
    try {
      setLoading(true)

      let actionRequests = await getRequests(
        {
          requestableType: RequestableType.DEVICE,
          requestableId: deviceName,
          action: ActionRequestAction.ENABLE_ALERT_NOTIFICATIONS,
          status: RequestStatus.REQUESTED
        },
        {
          signal
        }
      )

      // Filter action request that are older than 24 hours.
      const now = new Date()
      const twentyFourHoursAgo = subHours(now, 24)
      actionRequests = actionRequests.filter(
        (actionRequest) => new Date(actionRequest.createdAt) >= twentyFourHoursAgo
      )

      setActionRequests(actionRequests)
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the ruuvitag action requests.
   */
  async function loadRuuviTagActionRequests(ruuviTagId: string, signal: AbortSignal) {
    try {
      setLoading(true)

      let actionRequests = await getRequests(
        {
          requestableType: RequestableType.RUUVI_TAG,
          requestableId: ruuviTagId,
          action: ActionRequestAction.ENABLE_ALERT_NOTIFICATIONS,
          status: RequestStatus.REQUESTED
        },
        {
          signal
        }
      )

      // Filter action request that are older than 24 hours.
      const now = new Date()
      const twentyFourHoursAgo = subHours(now, 24)
      actionRequests = actionRequests.filter(
        (actionRequest) => new Date(actionRequest.createdAt) >= twentyFourHoursAgo
      )

      setActionRequests(actionRequests)
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Remove the action request.
   */
  function removeActionRequest(actionRequest: ActionRequest) {
    setActionRequests((prev) => prev.filter((a) => a.id !== actionRequest.id))
  }

  /**
   * Handle the "after submit" action.
   */
  function handleAfterSubmit(actionRequest: ActionRequest) {
    removeActionRequest(actionRequest)
    afterAccept?.(actionRequest)
  }

  /**
   * Handle the notification.
   */
  function handleNotification(notification: Notification<ActionRequest>) {
    const actionRequest = notification.body.data

    const isThisDeviceRequest =
      device &&
      actionRequest.requestableType === RequestableType.DEVICE &&
      actionRequest.requestableId === device?.name

    const isThisRuuviTagRequest =
      ruuviTag &&
      actionRequest.requestableType === RequestableType.RUUVI_TAG &&
      actionRequest.requestableId === ruuviTag.id

    if (!isThisDeviceRequest && !isThisRuuviTagRequest) {
      return
    }

    if (
      notification.action === EntityEventAction.RemoveActionRequest ||
      notification.action === EntityEventAction.SoftRemoveActionRequest ||
      (notification.action === EntityEventAction.UpdateActionRequest &&
        isActionRequestClosed(actionRequest))
    ) {
      // Remove the action request.
      removeActionRequest(actionRequest)
    }
  }

  /**
   * Load action requests when the device name changes.
   */
  useEffect(() => {
    const controller = new AbortController()
    if (device?.name) {
      loadDeviceActionRequests(device.name, controller.signal)
    } else if (ruuviTag?.id) {
      loadRuuviTagActionRequests(ruuviTag.id, controller.signal)
    }
    return () => controller.abort()
  }, [device?.name, ruuviTag?.id])

  /**
   * Subscribe to action request notifications.
   */
  subscribeActionRequestNotifications(handleNotification)

  return (
    <>
      {actionRequest && (
        <Alert severity="warning" {...props}>
          <AlertTitle>
            {t('EnableAlertNotifications.title', {
              device: device
                ? (device?.asset?.name ?? device.name)
                : ruuviTag
                  ? (ruuviTag?.asset?.name ?? ruuviTag?.id)
                  : '-'
            })}
          </AlertTitle>

          <Typography>{t('EnableAlertNotifications.message')}</Typography>

          <ActionRequestAcceptButton
            actionRequest={actionRequest}
            label={t('EnableAlertNotifications.action')}
            size="small"
            variant="contained"
            color="warning"
            sx={{ mt: 2 }}
            afterSubmit={handleAfterSubmit}
          />
        </Alert>
      )}
    </>
  )
}
